import React, { useEffect, useState } from "react";
import { Calendar, Clock, Users, Check, AlertTriangle, RefreshCw, Sheet, Plus, Link2, Loader2 } from "lucide-react";
import { Card, CardContent, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { ColumnSelect } from "../ui/select";
import GoogleSheetsPicker from "../GoogleSheetsPicker";
import { connectGoogleSheet, createGoogleSheet, getGoogleSheetsAuthStatus, getGoogleSheetsAuthorizationUrl, getGoogleSheetTabs, SheetTab } from "@/lib/googleSheetsApi";
import {
  PERIODICITY_PRESETS,
  PeriodicityUnitUpper,
  formatCustomRepeatPreview,
  getCustomPeriodicityMax,
  getReminderMaxDays,
  isPresetPeriodicity,
  validatePeriodicityValue,
} from "./renewalUtils";

interface StepDataProps {
  // Campaign info
  campaignName?: string;

  googleSheetId: string;
  googleSheetTabName: string;
  selectSheetTab: (tabName: string, tabId?: number | null) => void;
  excludedTabNames?: string[];
  excludedTabIds?: number[];
  sheetColumns: string[] | null;
  columnValidationStatus: "idle" | "loading" | "loaded" | "error";
  phoneColumn: string;
  setPhoneColumn: (value: string) => void;
  renewalDateColumn: string;
  setRenewalDateColumn: (value: string) => void;
  periodicityUnit: PeriodicityUnitUpper;
  setPeriodicityUnit: (unit: PeriodicityUnitUpper) => void;
  periodicityValue: number;
  setPeriodicityValue: (value: number) => void;
  onPeriodicityChange?: (unit: PeriodicityUnitUpper, value: number) => void;
  isCustomPeriodicity?: boolean;
  onEnableCustomPeriodicity?: () => void;
  onDisableCustomPeriodicity?: () => void;
  onCustomPeriodicityChange?: (unit: PeriodicityUnitUpper, value: number) => void;
  reminderDays: number[];
  reminderInput: string;
  setReminderInput: (value: string) => void;
  addReminderFromInput: (input?: string) => void;
  removeReminder: (value: number) => void;
  sendingTime: string; // ✅ NUEVO: Hora de envío desde el hook padre
  setSendingTime: (value: string) => void; // ✅ NUEVO
  loadingAssets: boolean;
  sheets: any[];
  visibleSheets: any[];
  total: number;
  totalPages: number;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  start: number;
  selectSheet: (id: string) => void;
  reloadColumns?: () => Promise<void>;
  reloadSheets?: () => Promise<any[]>;
  showTutorialModal: boolean;
  setShowTutorialModal: (value: boolean) => void;
  onBack: () => void;
  onNext: () => void;
}


const EMPTY_EXCLUDED_TAB_NAMES: string[] = [];
const EMPTY_EXCLUDED_TAB_IDS: number[] = [];

const StepData: React.FC<StepDataProps> = ({
  campaignName,
  googleSheetId, googleSheetTabName, selectSheetTab,
  excludedTabNames = EMPTY_EXCLUDED_TAB_NAMES,
  excludedTabIds = EMPTY_EXCLUDED_TAB_IDS,
  sheetColumns, columnValidationStatus, phoneColumn, setPhoneColumn,
  renewalDateColumn, setRenewalDateColumn,
  periodicityUnit, setPeriodicityUnit, periodicityValue, setPeriodicityValue, onPeriodicityChange,
  isCustomPeriodicity = false,
  onEnableCustomPeriodicity,
  onDisableCustomPeriodicity,
  onCustomPeriodicityChange,
  reminderDays, reminderInput, setReminderInput, addReminderFromInput, removeReminder,
  sendingTime, setSendingTime, // ✅ NUEVO: Hora de envío conectada al hook padre
  loadingAssets, sheets, visibleSheets, total, totalPages, currentPage, setCurrentPage, start,
  selectSheet, reloadColumns, reloadSheets, showTutorialModal, setShowTutorialModal, onBack, onNext
}) => {
  const [reminderError, setReminderError] = useState<string>("");
  const [reloadingColumns, setReloadingColumns] = useState(false);
  const [pickerAutoOpen, setPickerAutoOpen] = useState(false);
  const [showCreateSheetModal, setShowCreateSheetModal] = useState(false);
  const [newSheetName, setNewSheetName] = useState("");
  const [creatingSheet, setCreatingSheet] = useState(false);
  const [sheetTabs, setSheetTabs] = useState<SheetTab[]>([]);
  const [loadingSheetTabs, setLoadingSheetTabs] = useState(false);
  const currentSheetIdRef = React.useRef(googleSheetId);
  currentSheetIdRef.current = googleSheetId;
  const refreshTabsRequestRef = React.useRef(0);

  // ✅ Eliminado: sendTime ahora viene de las props (sendingTime, setSendingTime)
  const [errors, setErrors] = useState<{
    sheet?: string;
    phoneColumn?: string;
    renewalDateColumn?: string;
    periodicity?: string;
    reminders?: string;
    sendTime?: string;
  }>({});
  // Track whether the user has explicitly selected a periodicity option.
  // Initialize to true if parent props already have a valid selection (e.g., when navigating back).
  const [periodicitySet, setPeriodicitySet] = useState<boolean>(() => {
    if (isCustomPeriodicity) return validatePeriodicityValue(periodicityUnit, periodicityValue) === null;
    return isPresetPeriodicity(periodicityUnit, periodicityValue);
  });
  const [customIntervalInput, setCustomIntervalInput] = useState<string>(() =>
    periodicityValue > 0 ? String(periodicityValue) : ""
  );

  useEffect(() => {
    if (!googleSheetId) {
      setSheetTabs([]);
      return;
    }

    let cancelled = false;

    const loadSheetTabs = async () => {
      try {
        setLoadingSheetTabs(true);

        const tabs = await getGoogleSheetTabs(googleSheetId);
        const visibleTabs = tabs.filter(
          (tab) =>
            !tab.hidden &&
            !excludedTabIds.includes(tab.sheet_id) &&
            !excludedTabNames.includes(tab.title)
        );

        if (!cancelled) {
          setSheetTabs(visibleTabs);
        }
      } catch (error) {
        console.error("Error cargando pestañas de Google Sheets:", error);

        if (!cancelled) {
          setSheetTabs([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingSheetTabs(false);
        }
      }
    };

    void loadSheetTabs();

    return () => {
      cancelled = true;
    };
  }, [googleSheetId, excludedTabNames, excludedTabIds])

  
  const refreshTabsOnOpen = async () => {
      const sheetId = googleSheetId;
      if (!sheetId) return;

      const requestId = ++refreshTabsRequestRef.current;

      try {
          const tabs = await getGoogleSheetTabs(sheetId);

          if (
              currentSheetIdRef.current !== sheetId ||
              refreshTabsRequestRef.current !== requestId
          ) {
              return;
          }

          const visibleTabs = tabs.filter(
              (tab) =>
                  !tab.hidden &&
                  !excludedTabIds.includes(tab.sheet_id) &&
                  !excludedTabNames.includes(tab.title)
          );

          setSheetTabs(visibleTabs);
      } catch (error) {
          if (
              currentSheetIdRef.current !== sheetId ||
              refreshTabsRequestRef.current !== requestId
          ) {
              return;
          }

          console.error("Error actualizando pestañas:", error);
      }
  };


  useEffect(() => {
    if (isCustomPeriodicity) {
      setPeriodicitySet(true);
      setCustomIntervalInput(periodicityValue > 0 ? String(periodicityValue) : "");
    }
  }, [isCustomPeriodicity]);
  // Cleanup any intro.js DOM residue from a previous tour instance
  useEffect(() => {
    try {
      document.querySelectorAll(
        '.introjs-overlay, .introjs-helperLayer, .introjs-tooltipReferenceLayer'
      ).forEach(el => el.remove());
      document.body.classList.remove('introjs-fixParent', 'introjs-noScroll');
    } catch { }
  }, []);

  useEffect(() => {
    if (!showTutorialModal) return;

    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prev = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      width: document.body.style.width,
    };

    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.width = "100%";

    return () => {
      document.body.style.overflow = prev.overflow;
      document.body.style.position = prev.position;
      document.body.style.top = prev.top;
      document.body.style.left = prev.left;
      document.body.style.width = prev.width;
      window.scrollTo(0, scrollY);
    };
  }, [showTutorialModal]);

  useEffect(() => {
    if (loadingAssets) return;

    const hasSheets = sheets.length > 0;
    const flagName = hasSheets ? 'renewal_tour_stage2_seen' : 'renewal_tour_stage1_seen';

    const triggerStageScheduling = () => {
      return setTimeout(() => {
        import('../common/RenewalCampaignTour').then(({ startRenewalSchedulingTour }) => {
          startRenewalSchedulingTour(() => {
            sessionStorage.setItem('renewal_tour_stage_scheduling_seen', 'true');
          });
        }).catch(e => console.error('Error cargando Scheduling Tour:', e));
      }, 300);
    };

    // Accepts current column values explicitly to avoid stale closures
    const triggerStageColumns = (currentPhone: string, currentDate: string) => {
      return setTimeout(() => {
        import('../common/RenewalCampaignTour').then(({ startRenewalColumnsTour }) => {
          startRenewalColumnsTour(() => {
            sessionStorage.setItem('renewal_tour_stage_columns_seen', 'true');
            // Use the passed-in values, not the closure values
            if (currentPhone && currentDate) {
              const hasSeenScheduling = sessionStorage.getItem('renewal_tour_stage_scheduling_seen');
              if (!hasSeenScheduling) triggerStageScheduling();
            }
          });
        }).catch(e => console.error('Error cargando Columns Tour:', e));
      }, 300);
    };

    const triggerStage1o2 = (currentPhone: string, currentDate: string) => {
      return setTimeout(() => {
        import('../common/RenewalCampaignTour').then(({ startRenewalCampaignTour }) => {
          startRenewalCampaignTour(hasSheets, () => {
            sessionStorage.setItem(flagName, 'true');
            if (hasSheets && googleSheetId) {
              if (currentPhone && currentDate) {
                const hasSeenScheduling = sessionStorage.getItem('renewal_tour_stage_scheduling_seen');
                if (!hasSeenScheduling) triggerStageScheduling();
              } else {
                const hasSeenColumns = sessionStorage.getItem('renewal_tour_stage_columns_seen');
                if (!hasSeenColumns) triggerStageColumns(currentPhone, currentDate);
              }
            }
          });
        }).catch(e => console.error('Error cargando Renewal Tour:', e));
      }, 300);
    };

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    if (hasSheets && googleSheetId) {
      if (phoneColumn && renewalDateColumn) {
        if (periodicitySet) {
          const hasSeenReminders = sessionStorage.getItem('renewal_tour_stage_reminders_seen');
          if (!hasSeenReminders) {
            timeoutId = setTimeout(() => {
              import('../common/RenewalCampaignTour').then(({ startRenewalRemindersTour }) => {
                startRenewalRemindersTour(() => {
                  sessionStorage.setItem('renewal_tour_stage_reminders_seen', 'true');
                });
              });
            }, 300);
          }
        } else {
          const hasSeenScheduling = sessionStorage.getItem('renewal_tour_stage_scheduling_seen');
          if (!hasSeenScheduling) timeoutId = triggerStageScheduling();
        }
      } else {
        const hasSeenColumns = sessionStorage.getItem('renewal_tour_stage_columns_seen');
        if (!hasSeenColumns) timeoutId = triggerStageColumns(phoneColumn, renewalDateColumn);
      }
    } else {
      const hasSeen = sessionStorage.getItem(flagName);
      if (!hasSeen) timeoutId = triggerStage1o2(phoneColumn, renewalDateColumn);
    }

    const handleRestart = (e: Event) => {
      if (e instanceof CustomEvent && e.detail) e.detail.handled = true;
      sessionStorage.removeItem('renewal_tour_stage1_seen');
      sessionStorage.removeItem('renewal_tour_stage2_seen');
      sessionStorage.removeItem('renewal_tour_stage_columns_seen');
      sessionStorage.removeItem('renewal_tour_stage_scheduling_seen');
      sessionStorage.removeItem('renewal_tour_stage_reminders_seen');

      if (hasSheets && googleSheetId) {
        if (phoneColumn && renewalDateColumn) {
          if (periodicitySet) {
            timeoutId = setTimeout(() => {
              import('../common/RenewalCampaignTour').then(({ startRenewalRemindersTour }) => {
                startRenewalRemindersTour(() => {
                  sessionStorage.setItem('renewal_tour_stage_reminders_seen', 'true');
                });
              });
            }, 300);
          } else {
            timeoutId = triggerStageScheduling();
          }
        } else {
          timeoutId = triggerStageColumns(phoneColumn, renewalDateColumn);
        }
      } else {
        timeoutId = triggerStage1o2(phoneColumn, renewalDateColumn);
      }
    };

    window.addEventListener('restart-local-tour', handleRestart);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('restart-local-tour', handleRestart);
    };
  }, [sheets.length, loadingAssets, googleSheetId, phoneColumn, renewalDateColumn, periodicitySet]);


  const handleAddFromInput = (input?: string) => {
    const raw = (input ?? reminderInput ?? '').trim();
    if (!raw) {
      setReminderError('Introduce un número válido');
      return;
    }
    // support comma-separated list
    const parts = raw.split(',').map(p => p.trim()).filter(Boolean);
    const nums = parts.map(p => parseInt(p, 10));
    if (nums.some(n => Number.isNaN(n) || n < 0)) {
      setReminderError('Introduce números enteros válidos (>= 0)');
      return;
    }
    const maxDays = getReminderMaxDays(periodicityUnit, periodicityValue);
    const invalid = nums.filter(n => n > maxDays);
    if (invalid.length > 0) {
      setReminderError(`No puedes agregar ${invalid.join(', ')} (máx ${maxDays} días para la periodicidad seleccionada)`);
      return;
    }
    // all good — call parent adder with raw input
    addReminderFromInput(raw);
    setReminderInput('');
    setReminderError('');
    setErrors(prev => ({ ...prev, reminders: undefined }));
  };

  const handlePickerSheetsSelected = async (
    selectedSheets: Array<{ id: string }>
  ): Promise<void> => {
    if (selectedSheets.length === 0) {
      setPickerAutoOpen(false);
      return;
    }

    try {

      for (const sheet of selectedSheets) {
        try {
          await connectGoogleSheet(sheet.id);
        } catch (error) {
          console.error(`Error conectando hoja ${sheet.id}:`, error);
        }
      }

      const updatedSheets = await reloadSheets?.() ?? [];
      const selectedId = selectedSheets[selectedSheets.length - 1]?.id;

      const connectedSheet = updatedSheets.find((sheet: any) =>
        String(sheet.sheet_id ?? sheet.id ?? sheet._id ?? '') === String(selectedId)
      );

      if (connectedSheet && selectedId) {
        await selectSheet(selectedId);
      }
    } catch (error) {
      console.error('Error conectando hoja desde Google Picker:', error);
      alert('No se pudo conectar la hoja seleccionada. Por favor intenta de nuevo.');
    } finally {
      setPickerAutoOpen(false);
    }
  };

  const createNewSheet = async (): Promise<void> => {
    const trimmedName = newSheetName.trim();

    if (!trimmedName) return;

    setCreatingSheet(true);

    try {
      const data = await createGoogleSheet(trimmedName);

      const newSheetId = String(
        data?.sheet_id ?? data?.id ?? ''
      );

      await reloadSheets?.();

      if (newSheetId) {
        await selectSheet(newSheetId);
        setErrors(prev => ({ ...prev, sheet: undefined }));
      }

      setNewSheetName("");
      setShowCreateSheetModal(false);
    } catch (error) {
      console.error('Error creando nueva hoja de cálculo:', error);
      alert('No se pudo crear la hoja de cálculo. Por favor intenta de nuevo.');
    } finally {
      setCreatingSheet(false);
    }
  };

  const handleConnectSheets = async (showErrorAlert = false): Promise<void> => {
    localStorage.setItem('renewal_create_campaign_type', 'Renovación');
    if (campaignName) {
      localStorage.setItem('renewal_create_campaign_name', campaignName);
    }

    try {
      const authStatus = await getGoogleSheetsAuthStatus();
      if (authStatus.is_authenticated && !authStatus.needs_auth) {
        setPickerAutoOpen(true);
        return;
      }

      const authorizationUrl = await getGoogleSheetsAuthorizationUrl('renewal-campaign');
      if (!authorizationUrl) {
        throw new Error('No authorization URL received');
      }

      window.location.href = authorizationUrl;
    } catch (error) {
      console.error('Error:', error);
      if (showErrorAlert) {
        alert('Error al conectar con Google Sheets. Por favor intenta de nuevo.');
      }
    }
  };

  return (
    <Card className="mt-6">
      {pickerAutoOpen && (
        <GoogleSheetsPicker
          onSheetsSelected={handlePickerSheetsSelected}
          onPickerClosed={() => setPickerAutoOpen(false)}
          autoOpen={true}
          className="hidden"
        />
      )}

      {showCreateSheetModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-5">
              <h2 className="text-xl font-bold text-gray-900">
                Nueva Hoja de Cálculo
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Ingresa el nombre de la nueva hoja de cálculo.
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                void createNewSheet();
              }}
            >
              <div className="mb-6">
                <label
                  htmlFor="new-sheet-name"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Nombre de la hoja
                </label>

                <input
                  id="new-sheet-name"
                  type="text"
                  value={newSheetName}
                  onChange={(e) => setNewSheetName(e.target.value)}
                  placeholder="Ej. Clientes octubre 2026"
                  autoFocus
                  disabled={creatingSheet}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-900 outline-none transition focus:border-[#1CB3B3] focus:ring-2 focus:ring-[#1CB3B3]/20 disabled:bg-gray-100"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  disabled={creatingSheet}
                  onClick={() => {
                    setNewSheetName("");
                    setShowCreateSheetModal(false);
                  }}
                >
                  Cancelar
                </Button>

                <Button
                  type="submit"
                  disabled={!newSheetName.trim() || creatingSheet}
                  className="bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white"
                >
                  {creatingSheet ? "Creando..." : "Crear Hoja"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <CardContent className="space-y-6 p-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full flex-none bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] text-white flex items-center justify-center">
            <Calendar className="w-5 h-5" />
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">Seleccionar Datos</div>
            <p className="text-xs text-gray-400">Conecta tu hoja de cálculo y configura su envío</p>
          </div>
        </div>
        {/* Preparar tu hoja - Diseño elegante */}
        <div className="mt-6 relative rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
          <div className="flex items-start gap-5 pl-4">
            {/* Contenido */}
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-gray-900 mb-1">Prepara tu hoja de cálculo</h4>
              <p className="text-xs text-gray-600 leading-relaxed mb-4">
                Antes de continuar, asegúrate de que tu hoja tenga las columnas correctas configuradas
                (teléfono y fecha de renovación).
              </p>
              <button
                id="tour-renewal-guide"
                onClick={() => setShowTutorialModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-sm font-semibold text-white shadow-[0_4px_20px_rgba(28,179,179,0.4)] hover:shadow-[0_6px_25px_rgba(28,179,179,0.5)] transition-all animate-[pulse_2s_ease-in-out_infinite] hover:animate-none"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                Ver guía paso a paso
              </button>
            </div>
          </div>
        </div>

        {/* Selecciona tu hoja de cálculo */}
        <Card className="mt-4 group relative rounded-xl border border-gray-200 bg-white p-4 mb-4 shadow-[0_8px_30px_rgba(12,20,30,0.06)]">
          <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
          <CardContent className="pl-4">
            <div className="mb-3">
              <Label className="text-lg font-semibold mb-0">Selecciona tu hoja de cálculo</Label>
            </div>
            {errors.sheet && <p id="sheet-error" className="text-sm text-red-600 mt-2">{errors.sheet}</p>}
            {googleSheetId && googleSheetTabName && !sheetColumns && <div className="text-xs text-gray-500 mt-2">Cargando columnas de la pestaña...</div>}
            <div className="mt-2" aria-describedby={errors.sheet ? 'sheet-error' : undefined}>
              {loadingAssets ? (
                <div className="text-xs text-gray-500 mt-1">Cargando hojas...</div>
              ) : sheets.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-[#e6f7f4] to-[#f0f9fa] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#e0f2f1]">
                    <Sheet className="h-8 w-8 text-[#1CB3B3]" />
                  </div>
                  <h3 className="font-medium mb-2">No hay hojas conectadas</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Primero debes conectar al menos una hoja de Google Sheets para continuar con la creación de tu campaña.
                  </p>
                  <Button
                    id="tour-renewal-connect"
                    onClick={() => void handleConnectSheets(true)}
                    className="bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-6 h-11 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95"
                  >
                    Conectar Google Sheets
                  </Button>
                </div>
              ) : (
                <div>
                  <div id="tour-renewal-sheets-grid" className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {visibleSheets.map((sheet: any, idx: number) => {
                      const id = sheet.sheet_id ?? sheet.id ?? sheet._id ?? String(idx);
                      const lastModified = sheet.last_modified || sheet.updated_at || sheet.created_at;
                      const modifiedDate = lastModified ? new Date(lastModified) : null;
                      const isSelected = String(googleSheetId) === String(id);
                      return (
                        <div
                          key={id}
                          className={`border-2 rounded-lg p-4 cursor-pointer transition-all relative ${isSelected
                            ? "border-[#1CB3B3] bg-gradient-to-r from-[#1CB3B3]/10 to-[#179999]/10 ring-1 ring-[#1CB3B3]/20"
                            : "border-gray-200 hover:border-gray-300 hover:shadow-md"
                            }`}
                          onClick={() => { selectSheet(id); setErrors(prev => ({ ...prev, sheet: undefined })); }}
                        >
                          <div className="flex justify-end mb-3 sm:absolute sm:right-3 sm:top-3 sm:mb-0">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); const url = sheet.url || `https://docs.google.com/spreadsheets/d/${sheet.sheet_id || sheet.id}`; window.open(url, "_blank", "noopener,noreferrer"); }}
                              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white border border-[#1CB3B3] text-[#0D7377] text-xs font-medium hover:bg-[#E8F7F7] transition-all"
                              title="Abrir hoja en Google Sheets"
                            >
                              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" /></svg>
                              Abrir hoja
                            </button>
                          </div>
                          <h4 className="font-medium text-gray-900 mb-1 break-words sm:pr-28">
                            {sheet.sheet_name || sheet.name || String(id)}
                          </h4>
                          <div className="flex flex-col gap-1">
                            {modifiedDate && (
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <Clock className="h-3 w-3" /> Modificado: {modifiedDate.toLocaleDateString("es-ES", { year: "numeric", month: "short", day: "numeric" })}
                              </p>
                            )}
                            {(sheet.row_count !== undefined && sheet.row_count > 0) && (
                              <p className="text-xs text-gray-500 flex items-center gap-1">
                                <Users className="h-3 w-3" /> {sheet.row_count} contactos
                              </p>
                            )}
                          </div>
                          {isSelected && (
                            <div className="inline-flex items-center gap-1 mt-2 bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white text-xs px-2 py-1 rounded shadow-sm">
                              <Check className="h-3 w-3" /> Seleccionada
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {total > 6 && (
                    <div className="flex items-center justify-between mt-4">
                      <div className="text-sm text-gray-600">{Math.min(start + 1, total)}-{Math.min(start + visibleSheets.length, total)} de {total} hojas</div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1}>←</Button>
                        <div className="px-3 py-1 rounded bg-gray-100 text-sm">{currentPage} / {totalPages}</div>
                        <Button variant="outline" size="sm" onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage >= totalPages}>→</Button>
                      </div>
                    </div>
                  
                  )}

                  
                  {googleSheetId && (
                    <div className="mt-4 pt-3 border-t border-gray-100">
                      <Label className="text-base font-semibold mb-0">
                        Selecciona la pestaña
                      </Label>
                      <div className="text-xs text-gray-500 mt-1 mb-2">
                        Elige la pestaña interna de Google Sheets que contiene los datos de esta campaña.
                      </div>

                      {loadingSheetTabs ? (
                        <div className="text-xs text-gray-500">
                          Cargando pestañas...
                        </div>
                      ) : (
                        <ColumnSelect
                          options={sheetTabs.map((tab) => tab.title)}
                          value={googleSheetTabName || ""}
                          onChange={(tabName) => {
                            const selectedName = tabName || "";
                            const selectedTab = sheetTabs.find(
                              (tab) => tab.title === selectedName
                            );

                            selectSheetTab(
                              selectedName,
                              selectedTab?.sheet_id ?? null
                            );

                            setErrors(prev => ({
                              ...prev,
                              sheet: undefined
                            }));
                          }}
                          onOpen={() => void refreshTabsOnOpen()}
                          placeholder="Seleccionar pestaña..."
                        />
                      )}

                      {!loadingSheetTabs && sheetTabs.length === 0 && (
                        <p className="text-xs text-amber-600 mt-2">
                          No se encontraron pestañas disponibles en esta hoja de cálculo.
                        </p>
                      )}
                    </div>
                  )}


                  {/* Botón para conectar otra hoja */}
                  <div className="flex flex-col lg:flex-row justify-center gap-3 mt-6 pt-4 border-t border-gray-100">
                    <Button
                      onClick={() => void handleConnectSheets()}
                     className="w-full sm:w-auto bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-6 h-11 rounded-xl shadow-lg flex items-center justify-center gap-2 font-semibold transition-all active:scale-95"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                        Conectar Hojas
                    </Button>
                    <Button
                      onClick={() => setShowCreateSheetModal(true)}
                      disabled={creatingSheet}
                      className="w-full sm:w-auto min-w-0 bg-gradient-to-r from-[#1F4068] to-[#1CB3B3] hover:from-[#1a3558] hover:to-[#189999] text-white px-3 sm:px-6 min-h-11 h-auto py-2.5 rounded-xl shadow-lg flex items-center justify-center gap-2 font-semibold text-center whitespace-normal leading-tight transition-all active:scale-95"
                    >
                      <Plus className="w-4 h-4 shrink-0" />
                      Nueva Hoja de Cálculo
                    </Button>
                  </div>
                  {/* Mostrar advertencia si la hoja está vacía, o selector de columnas si tiene datos */}
                  {googleSheetId && googleSheetTabName &&
                    (columnValidationStatus === "loading" || columnValidationStatus === "loaded") && (
                    <div id="tour-renewal-columns" className="mt-6 pt-4 border-t border-gray-100 bg-white rounded-xl p-2">
                      <div className="mb-3">
                        <Label className="text-lg font-semibold mb-0">Asignar columnas</Label>
                        <div className="text-xs text-gray-500">Selecciona cuál columna es el teléfono y cuál la fecha de renovación</div>
                      </div>

                      {columnValidationStatus === "loading" ? (
                          <div className="flex items-center gap-2 text-gray-500 py-4">
                              <Loader2 className="w-5 h-5 animate-spin" />
                              <span className="text-xs">Cargando columnas...</span>
                          </div>
                      ) : (!sheetColumns || sheetColumns.length < 2) ? (
                        <div className="p-3 sm:p-4 bg-white border border-gray-100 rounded-xl flex flex-col sm:flex-row items-start gap-3 sm:gap-4 shadow-sm min-w-0">
                          <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-amber-800">
                              {(!sheetColumns || sheetColumns.length === 0) ? "La hoja está vacía" : "Datos insuficientes"}
                            </div>
                            <div className="text-xs text-amber-600 mt-1">
                              Necesitas añadir datos a tu hoja de cálculo antes de continuar.
                              Asegúrate de incluir una columna con los números de <strong>teléfono</strong> y otra con la <strong>fecha de renovación</strong>.
                            </div>
                            <div className="flex flex-col sm:flex-row gap-2 mt-3">
                              <button
                                type="button"
                                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#1CB3B3] text-[#0D7377] text-sm font-medium hover:bg-[#E8F7F7] transition-all"
                                onClick={() => {
                                  const sheet = visibleSheets.find((s: any) => (s.sheet_id ?? s.id) === googleSheetId);
                                  const url = sheet?.url || `https://docs.google.com/spreadsheets/d/${googleSheetId}`;
                                  window.open(url, "_blank", "noopener,noreferrer");
                                }}
                              >
                                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" /></svg>
                                Abrir hoja
                              </button>
                              {reloadColumns && (
                                <button
                                  type="button"
                                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white text-sm font-medium hover:shadow-lg transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                  disabled={reloadingColumns}
                                  onClick={async () => {
                                    setReloadingColumns(true);
                                    // Clear selections
                                    setPhoneColumn("");
                                    setRenewalDateColumn("");
                                    try {
                                      await reloadColumns();
                                    } finally {
                                      setReloadingColumns(false);
                                    }
                                  }}
                                >
                                  <RefreshCw
                                    className={`w-4 h-4 shrink-0 ${reloadingColumns ? 'animate-spin' : ''}`}
                                  />
                                  {reloadingColumns ? 'Actualizando...' : 'Actualizar columnas'}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <div>
                              <div aria-invalid={!!errors.phoneColumn} aria-describedby={errors.phoneColumn ? 'phoneColumn-error' : undefined}>
                                <ColumnSelect
                                  label="Columna teléfono"
                                  options={(sheetColumns ?? []).filter(c => c !== renewalDateColumn)} // Excluir columna seleccionada en renovación
                                  value={phoneColumn}
                                  onChange={(v: any) => { setPhoneColumn(v); setErrors(prev => ({ ...prev, phoneColumn: undefined })); }}
                                  placeholder="Selecciona columna de teléfono"
                                />
                              </div>
                              {errors.phoneColumn && <p id="phoneColumn-error" className="text-sm text-red-600 mt-2">{errors.phoneColumn}</p>}
                            </div>
                            <div>
                              <div aria-invalid={!!errors.renewalDateColumn} aria-describedby={errors.renewalDateColumn ? 'renewalDateColumn-error' : undefined}>
                                <ColumnSelect
                                  label="Columna fecha de renovación"
                                  options={(sheetColumns ?? []).filter(c => c !== phoneColumn)} // Excluir columna seleccionada en teléfono
                                  value={renewalDateColumn}
                                  onChange={(v: any) => { setRenewalDateColumn(v); setErrors(prev => ({ ...prev, renewalDateColumn: undefined })); }}
                                  placeholder="Selecciona columna de fecha de renovación"
                                />
                              </div>
                              {errors.renewalDateColumn && <p id="renewalDateColumn-error" className="text-sm text-red-600 mt-2">{errors.renewalDateColumn}</p>}
                            </div>
                          </div>

                          {/* Botón para actualizar columnas */}
                          <div className="flex justify-center pt-2">
                            {reloadColumns && (
                              <button
                                type="button"
                                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#1CB3B3] text-[#0D7377] text-xs font-medium hover:bg-[#E8F7F7] transition-all disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap"
                                disabled={reloadingColumns}
                                onClick={async () => {
                                  setReloadingColumns(true);
                                  setPhoneColumn("");
                                  setRenewalDateColumn("");
                                  try {
                                    await reloadColumns();
                                  } finally {
                                    setReloadingColumns(false);
                                  }
                                }}
                              >
                                <RefreshCw
                                  className={`w-3.5 h-3.5 shrink-0 ${reloadingColumns ? 'animate-spin' : ''}`}
                                />
                                {reloadingColumns ? 'Actualizando...' : 'Actualizar columnas'}
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        {/* Scheduling */}
        {googleSheetId && sheetColumns && sheetColumns.length > 0 && (
          <>
            {/* Línea exterior — igual que en la sección "Selecciona tus Google Sheets" */}
            <div id="tour-renewal-scheduling" className="relative mt-6 rounded-xl border border-gray-200 bg-white shadow-md p-4">
              <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
              <div className="pl-4">
                <div className="mb-3">
                  <Label className="text-lg font-semibold mb-0">Frecuencia de Renovación</Label>
                  <div className="text-xs text-gray-500">¿Cada cuánto tiempo quieres que se repita este recordatorio de renovación?</div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                  <div className="lg:col-span-2">
                    <Label className="text-sm">Periodicidad</Label>
                    <div className="mt-2 w-full">
                      <div role="tablist" aria-label="Opciones de periodicidad" aria-describedby={errors.periodicity ? 'periodicity-error' : undefined} aria-invalid={!!errors.periodicity} className="flex gap-2 flex-wrap">
                        {PERIODICITY_PRESETS.map((opt) => {
                          const active = !isCustomPeriodicity && opt.unit === periodicityUnit && opt.value === periodicityValue;
                          return (
                            <button
                              key={opt.key}
                              type="button"
                              aria-pressed={active}
                              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]/25 ${active ? 'bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white shadow-sm' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}${isCustomPeriodicity ? ' opacity-60' : ''}`}
                              onClick={() => {
                                const newUnit = opt.unit;
                                const newValue = opt.value;
                                if (onPeriodicityChange) {
                                  onPeriodicityChange(newUnit, newValue);
                                } else {
                                  setPeriodicityUnit(newUnit);
                                  setPeriodicityValue(newValue);
                                }
                                setPeriodicitySet(true);
                                setErrors(prev => ({ ...prev, periodicity: undefined }));
                              }}
                            >
                              {opt.key}
                            </button>
                          );
                        })}
                        <button
                          type="button"
                          aria-pressed={isCustomPeriodicity}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-[#1CB3B3]/25 ${isCustomPeriodicity ? 'bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white shadow-sm' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                          onClick={() => {
                            onEnableCustomPeriodicity?.();
                            setPeriodicitySet(true);
                            setCustomIntervalInput(periodicityValue > 0 ? String(periodicityValue) : "1");
                            setErrors(prev => ({ ...prev, periodicity: undefined }));
                          }}
                        >
                          Personalizado...
                        </button>
                      </div>

                      {isCustomPeriodicity && (
                        <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50/80 p-4 space-y-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <Label className="text-sm text-gray-700 shrink-0">Repetir cada:</Label>
                            <input
                              type="number"
                              min={1}
                              inputMode="numeric"
                              className="w-20 h-10 px-3 border border-gray-200 rounded-xl bg-white text-sm shadow-sm"
                              value={customIntervalInput}
                              aria-label="Intervalo personalizado"
                              onChange={(e) => {
                                const raw = e.target.value;
                                setCustomIntervalInput(raw);
                                setPeriodicitySet(true);
                                if (raw.trim() === "") {
                                  onCustomPeriodicityChange?.(periodicityUnit, 0);
                                  setErrors(prev => ({ ...prev, periodicity: "Ingresa un número válido (mínimo 1)" }));
                                  return;
                                }
                                const parsed = Number.parseInt(raw, 10);
                                if (Number.isNaN(parsed)) {
                                  onCustomPeriodicityChange?.(periodicityUnit, 0);
                                  setErrors(prev => ({ ...prev, periodicity: "Ingresa un número válido (mínimo 1)" }));
                                  return;
                                }
                                // Keep the typed value even if over the 1-year max; show feedback below
                                onCustomPeriodicityChange?.(periodicityUnit, parsed);
                                const err = validatePeriodicityValue(periodicityUnit, parsed);
                                setErrors(prev => ({ ...prev, periodicity: err || undefined }));
                              }}
                            />
                            <div className="min-w-[140px] w-40" aria-label="Unidad de periodicidad">
                              <ColumnSelect
                                options={["Días", "Semanas", "Meses"]}
                                value={periodicityUnit === "WEEKS" ? "Semanas" : periodicityUnit === "DAYS" ? "Días" : "Meses"}
                                placeholder="Unidad"
                                onChange={(label: string) => {
                                  const newUnit: PeriodicityUnitUpper =
                                    label === "Días" ? "DAYS" : label === "Semanas" ? "WEEKS" : "MONTHS";
                                  const current = Number.parseInt(customIntervalInput || "0", 10);
                                  const nextValue = Number.isNaN(current) || current < 1 ? 0 : current;
                                  onCustomPeriodicityChange?.(newUnit, nextValue);
                                  const err = validatePeriodicityValue(newUnit, nextValue);
                                  setErrors(prev => ({ ...prev, periodicity: err || undefined }));
                                }}
                              />
                            </div>
                          </div>
                          {(() => {
                            const parsed = Number.parseInt(customIntervalInput || "0", 10);
                            const previewValue = Number.isNaN(parsed) ? 0 : parsed;
                            const overLimitErr = validatePeriodicityValue(periodicityUnit, previewValue);
                            if (overLimitErr && previewValue > getCustomPeriodicityMax(periodicityUnit)) {
                              return (
                                <p id="periodicity-error" className="text-xs text-red-600">
                                  {overLimitErr}
                                </p>
                              );
                            }
                            if (overLimitErr && (previewValue < 1 || Number.isNaN(parsed))) {
                              return (
                                <p id="periodicity-error" className="text-xs text-red-600">
                                  {overLimitErr}
                                </p>
                              );
                            }
                            return (
                              <p className="text-xs text-gray-500">
                                {formatCustomRepeatPreview(periodicityUnit, previewValue || periodicityValue)}
                              </p>
                            );
                          })()}
                        </div>
                      )}

                      {!isCustomPeriodicity && errors.periodicity && (
                        <p id="periodicity-error" className="text-sm text-red-600 mt-2">{errors.periodicity}</p>
                      )}
                    </div>
                  </div>
                  <div />
                </div>
              </div>
            </div>

            {/* Recordatorios - sección separada debajo (misma lógica y estilos) */}
            <Card id="tour-renewal-reminders" className="mt-6 relative"> {/* usar mt-6 para la misma separación vertical */}
              <div className="absolute left-0 top-0 bottom-0 w-1 rounded-l-xl bg-gradient-to-b from-[#1CB3B3] to-[#179999]" />
              <CardContent className="p-4">
                <div className="pl-4"> {/* agregar padding-left para alinear con la sección de 'Frecuencia' */}
                  {periodicityUnit !== 'DAYS' && (
                    <div className="mb-3">
                      <Label className="text-lg font-semibold mb-0">Recordatorios</Label>
                      <div className="text-xs text-gray-500">Indica cuántos días antes de la fecha de renovación registrada en la hoja de cálculo se enviarán los mensajes.</div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                    <div>
                      <div>
                        {periodicityUnit !== 'DAYS' && (
                          <>
                            <Label className="text-sm">Recordatorios (días antes)</Label>
                            <div className="flex flex-col lg:flex-row gap-2 mt-2">
                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                className="w-full min-w-0 sm:flex-1 h-10 px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm shadow-md"
                                placeholder="Ingresa un número"
                                value={reminderInput}
                                onChange={(e) => {
                                  setReminderInput(e.target.value.replace(/\D/g, ''));
                                  setReminderError('');
                                  setErrors(prev => ({ ...prev, reminders: undefined }));
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === ",") {
                                    e.preventDefault();
                                    handleAddFromInput();
                                  }
                                }}
                                aria-label="Introduce días antes"
                                aria-invalid={!!errors.reminders || !!reminderError}
                                aria-describedby={reminderError ? 'reminder-error' : (errors.reminders ? 'reminders-error' : undefined)}
                              />
                              <div className="flex items-center">
                                <Button
                                  onClick={() => { handleAddFromInput(); }}
                                  disabled={!reminderInput.trim()}
                                  className="w-full lg:w-auto bg-gradient-to-r from-[#1CB3B3] to-[#179999] text-white font-semibold px-4 py-2 rounded"
                                >
                                  Agregar
                                </Button>
                              </div>
                            </div>
                            {reminderError && <p id="reminder-error" className="text-sm text-red-600 mt-2">{reminderError}</p>}
                            {errors.reminders && <p id="reminders-error" className="text-sm text-red-600 mt-2">{errors.reminders}</p>}
                            <p className="text-xs text-gray-500 mt-2">0 significa que el recordatorio se enviará el mismo día de la fecha de renovación registrada en la hoja de cálculo.</p>
                          </>
                        )}
                      </div>
                      <div className={periodicityUnit !== 'DAYS' ? "mt-8" : ""}>
                        <Label className="text-sm">Hora de envío</Label>
                        <div className="mt-2">
                          <input
                            type="time"
                            value={sendingTime}
                            onChange={(e) => {
                              setSendingTime(e.target.value);
                              setErrors(prev => ({ ...prev, sendTime: undefined }));
                              try { window.dispatchEvent(new CustomEvent('analytics-event', { detail: { event: 'send_time_changed', time: e.target.value } })); } catch { }
                            }}
                            className="h-10 px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm shadow-md"
                            aria-label="Hora de envío"
                            aria-invalid={!!errors.sendTime}
                            aria-describedby={errors.sendTime ? 'sendtime-error' : undefined}
                          />
                          {errors.sendTime && <p id="sendtime-error" className="text-sm text-red-600 mt-2">{errors.sendTime}</p>}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">Selecciona la hora a la que quieres que se envíen los mensajes.</p>
                      </div>
                    </div>

                    {/* cuadro a la derecha: título separado y cuadro con estilo igual al izquierdo */}
                    {periodicityUnit !== 'DAYS' && (
                      <div>
                        <Label className="text-sm">Días seleccionados</Label>
                        {/* caja dinámica: conserva el tamaño de fila (text-sm, h-8) y expande su altura cuando hay más de 2 elementos */}
                        <div className="mt-2">
                          <div
                            className="rounded-xl border border-gray-200 bg-white shadow-md overflow-y-auto transition-[max-height] duration-150"
                            style={{
                              // Mostrar hasta 3 filas (cada fila ~40px) y activar scroll si hay más
                              maxHeight: '120px'
                            }}
                          >
                            <div className="p-2 h-full">
                              {reminderDays.length > 0 ? (
                                <div className="grid grid-cols-2 gap-2 items-center h-full">
                                  {[...reminderDays].sort((a, b) => b - a).map((d) => (
                                    <div key={d} className="flex items-center h-8 px-3 bg-gray-50 rounded-full text-sm text-gray-700 justify-between">
                                      <span className="truncate">
                                        {d === 0 ? 'mismo día' : `${d} ${d === 1 ? 'día' : 'días'} antes`}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => removeReminder(d)}
                                        className="ml-3 text-sm text-red-500 hover:text-red-600"
                                        aria-label={d === 0 ? 'Eliminar mismo día' : `Eliminar ${d} días antes`}
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="flex items-center justify-center h-full px-3">
                                  <p className="text-sm text-gray-400">Aún no hay recordatorios.</p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                    <div className="flex flex-col sm:flex-row items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-cyan-900 mb-2">
                          Información importante - Ley 2300 de 2023
                        </p>
                        <p className="text-xs text-cyan-800 mb-2">
                          Debido a la <strong>Ley 2300 de 2023</strong> (Ley "Dejen de Fregar Colombia"),
                          los horarios permitidos para el envío de mensajería o publicidad son:
                        </p>
                        <ul className="text-xs text-cyan-800 list-disc list-inside mb-2 space-y-1">
                          <li><strong>Lunes a viernes:</strong> 7:00 AM - 7:00 PM</li>
                          <li><strong>Sábados:</strong> 8:00 AM - 3:00 PM</li>
                          <li><strong>Domingos y festivos:</strong> Prohibido</li>
                        </ul>
                        <p className="text-xs text-cyan-700 italic">
                          No nos hacemos responsables por ningún tipo de inconveniente, multa o sanción
                          derivada del envío de mensajes fuera de los horarios permitidos por la ley.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </CardContent>

      {/* Footer: mantener botones Atrás y Siguiente con estilo consistente */}
      <CardFooter className="pt-6 border-t border-gray-100">
        <div className="w-full flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <Button
            variant="outline"
            onClick={onBack}
            className="w-full sm:w-auto border-gray-300 text-gray-600 hover:bg-gray-50 px-6 h-11 rounded-xl shadow-sm"
          >
            ← Atrás
          </Button>
          <Button onClick={() => {
            const newErrors: { sheet?: string; phoneColumn?: string; renewalDateColumn?: string; periodicity?: string; reminders?: string; sendTime?: string } = {};
            if (!googleSheetId) {
              newErrors.sheet = 'Selecciona una hoja de cálculo.';
            } else if (!googleSheetTabName) {
              newErrors.sheet = 'Selecciona una pestaña de Google Sheets.';
            } else if (!sheetColumns || sheetColumns.length === 0) {
              newErrors.sheet = 'La pestaña seleccionada está vacía. Añade datos con teléfono y fecha de renovación.';
            }

            if (googleSheetId && googleSheetTabName && sheetColumns && sheetColumns.length > 0) {
              if (!phoneColumn) newErrors.phoneColumn = 'Selecciona la columna de teléfono.';
              if (!renewalDateColumn) newErrors.renewalDateColumn = 'Selecciona la columna de fecha de renovación.';
            }
            if (!periodicitySet) newErrors.periodicity = 'Selecciona la frecuencia de renovación.';
            else if (isCustomPeriodicity) {
              const parsed = Number.parseInt(customIntervalInput || '', 10);
              const customErr = validatePeriodicityValue(periodicityUnit, Number.isNaN(parsed) ? null : parsed);
              if (customErr) newErrors.periodicity = customErr;
            }
            if (periodicityUnit !== 'DAYS' && (!reminderDays || reminderDays.length === 0)) newErrors.reminders = 'Agrega al menos un recordatorio.';
            if (!sendingTime) newErrors.sendTime = 'Selecciona la hora de envío.';
            setErrors(newErrors);
            if (Object.keys(newErrors).length === 0) {
              onNext();
            } else {
              const errEl = document.querySelector('.text-sm.text-red-600');
              if (errEl) (errEl as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }} className="w-full sm:w-auto bg-gradient-to-r from-[#1CB3B3] to-[#179999] hover:from-[#179999] hover:to-[#1CB3B3] text-white font-semibold px-8 h-11 rounded-xl shadow-lg hover:shadow-cyan-500/30 transform transition-all active:scale-95">
            Siguiente →
          </Button>
        </div>
      </CardFooter>

      {/* Tutorial Modal */}
      {showTutorialModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={() => setShowTutorialModal(false)}>
          <div className="relative bg-white rounded-xl shadow-2xl max-w-[95vw] w-full max-h-[95vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowTutorialModal(false)} aria-label="Cerrar guía" className="absolute top-3 right-3 z-20 bg-white/80 hover:bg-white rounded-full p-1 focus:outline-none">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <div className="flex items-center justify-center" style={{ padding: 12 }}>
              <img
                src="/assets/renovacion.png"
                alt="Guía paso a paso - Recordatorios Periódicos"
                className="rounded-md shadow-md"
                style={{ width: '100%', height: 'auto', maxHeight: 'calc(95vh - 24px)', objectFit: 'contain' }}
              />
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default StepData;